import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldWarning, ArrowRight, Cube, Hash } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Verify() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("t") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (params.get("t")) verify(params.get("t"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (t) => {
    if (!t) return;
    setLoading(true);
    setErr("");
    setResult(null);
    try {
      const { data } = await axios.get(`${BASE}/api/public/verify`, { params: { token: t } });
      setResult(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-[#0A0B0D]" data-testid="verify-page">
      {/* nav */}
      <nav className="border-b border-[#E5E5EA] bg-white">
        <div className="max-w-[1100px] mx-auto h-14 px-5 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0A0B0D] text-white font-display font-bold text-[13px] flex items-center justify-center">S</div>
            <div className="font-display font-bold text-[14px] tracking-tight">SPAN</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#475467] hidden sm:block">· Public verifier</div>
          </Link>
          <Link to="/" className="text-[12px] hover:text-[#0A0B0D]/70">← Home</Link>
        </div>
      </nav>

      <section className="border-b border-[#E5E5EA] py-14 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="label-eyebrow mb-3">SPAN PROOF · HMAC-SHA256 · NO LOGIN REQUIRED</div>
          <h1 className="font-display font-bold text-[40px] sm:text-[52px] leading-[0.98] tracking-tight">
            Verify any SPAN evidence<br />in <span className="bg-[#0A0B0D] text-white px-2">2 seconds.</span>
          </h1>
          <p className="text-[15px] text-[#475467] mt-5 max-w-[640px] leading-[1.6]">
            Paste a SPAN proof token (printed on every court bundle) to verify the evidence's
            integrity, on-chain anchor and chain of custody — without a SPAN account.
          </p>

          {/* form */}
          <div className="mt-9 border-2 border-black bg-white p-5 lg:p-7 max-w-[840px]">
            <label className="label-eyebrow block mb-2">Proof token</label>
            <textarea
              data-testid="verify-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste the SPAN proof token here…"
              rows={3}
              className="w-full border border-[#D0D5DD] p-3 text-[12px] font-mono"
            />
            <div className="flex items-center gap-3 mt-3">
              <button
                data-testid="verify-btn"
                onClick={() => verify(token)}
                disabled={!token || loading}
                className="span-btn"
              >
                {loading ? "Verifying…" : "Verify integrity"} <ArrowRight size={13} />
              </button>
              {err && <div className="text-[12px] text-red-600" data-testid="verify-error">{err}</div>}
            </div>
          </div>

          {/* result */}
          {result && (
            <div className="mt-9 max-w-[840px]" data-testid="verify-result">
              {result.valid ? (
                <div className="border-2 border-emerald-600 bg-emerald-50 p-5 lg:p-7">
                  <div className="flex items-center gap-3">
                    <ShieldCheck size={32} weight="fill" className="text-emerald-700" />
                    <div>
                      <div className="font-display font-bold text-[22px] text-emerald-900">Evidence integrity verified</div>
                      <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-emerald-700 mt-1">HMAC-SHA256 · INTACT · IMMUTABLE</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6 pt-5 border-t border-emerald-200">
                    {[
                      ["Case code", result.case_code || "—"],
                      ["Evidence type", result.evidence_type || "—"],
                      ["Captured by", result.captured_by || "—"],
                      ["Captured at", result.captured_at?.replace("T", " ").substring(0, 19) || "—"],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <div className="label-eyebrow text-[9px]">{k}</div>
                        <div className="font-display font-medium text-[14px] mt-1">{v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-5 border-t border-emerald-200">
                    <div className="flex items-center gap-2 mb-2"><Hash size={14}/><div className="label-eyebrow text-[9px]">SHA-256</div></div>
                    <div className="font-mono text-[11px] break-all text-emerald-900">{result.sha256}</div>
                  </div>
                  {result.anchor && (
                    <div className="mt-5 pt-5 border-t border-emerald-200">
                      <div className="flex items-center gap-2 mb-2"><Cube size={14}/><div className="label-eyebrow text-[9px]">Blockchain anchor</div></div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px]">
                        <div>
                          <div className="font-mono text-[10px] text-emerald-700">CHAIN</div>
                          <div className="font-mono mt-1">{result.anchor.chain}</div>
                        </div>
                        <div>
                          <div className="font-mono text-[10px] text-emerald-700">BLOCK</div>
                          <div className="font-mono mt-1 tnum">{result.anchor.block_number?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="font-mono text-[10px] text-emerald-700">ANCHORED</div>
                          <div className="font-mono mt-1">{result.anchor.anchored_at?.substring(0, 10)}</div>
                        </div>
                      </div>
                      <div className="font-mono text-[10px] mt-3 break-all opacity-70">{result.anchor.tx_hash}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-2 border-red-600 bg-red-50 p-7" data-testid="verify-invalid">
                  <div className="flex items-center gap-3">
                    <ShieldWarning size={32} weight="fill" className="text-red-700" />
                    <div>
                      <div className="font-display font-bold text-[22px] text-red-900">Verification failed</div>
                      <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-red-700 mt-1">Signature does not match · evidence has been altered or token is invalid</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-6">
          {[
            {n: "01", t: "Token-based proof", d: "Every SPAN evidence carries a single URL-safe token that travels in court bundles — no SPAN account needed to verify."},
            {n: "02", t: "Offline-verifiable", d: "HMAC-SHA256 over evidence_id + SHA-256 of file bytes + capture timestamp. Auditors can replicate the verification in 3 lines of Python."},
            {n: "03", t: "Tamper-evident chain", d: "Any change to the file, ID, or timestamp will fail the verification. Combined with blockchain anchor for third-party timestamping."},
          ].map((b) => (
            <div key={b.n} className="border border-[#E5E5EA] bg-white p-6">
              <div className="font-mono text-[10px] text-[#475467]">{b.n}</div>
              <div className="font-display font-bold text-[17px] mt-4">{b.t}</div>
              <div className="text-[13px] text-[#475467] mt-2 leading-[1.55]">{b.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
